/**
 * Catalog of Works UI standards — single source for shared layout and chrome classes.
 * Reference: AGENTS.md, .cursor/rules/ui-standards.mdc
 */

/** Global corner radius — use CSS vars from index.css (:root). Pills use rounded-full. */
export const radiusControlClass = 'rounded-[var(--radius-control)]';
export const radiusSurfaceClass = 'rounded-[var(--radius-surface)]';
export const radiusDialogClass = 'rounded-[var(--radius-dialog)]';

/** Bordered card / table shell (platform tenants, product list, …). */
export const surfacePanelClass = `${radiusSurfaceClass} border border-[var(--border-color)] bg-[var(--bg-primary)]`;

/** Settings tab: vertical stack of SettingsSection blocks. */
export const settingsTabStackClass = 'space-y-6';

export type SettingsSectionTone = 'neutral' | 'soft' | 'sky' | 'teal' | 'amber' | 'violet' | 'rose';

const SETTINGS_SECTION_SHELL: Record<SettingsSectionTone, string> = {
  neutral: `${radiusSurfaceClass} border border-[var(--border-color)] bg-[var(--bg-primary)] p-4 shadow-sm`,
  soft: `${radiusSurfaceClass} border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4 shadow-sm`,
  sky: `${radiusSurfaceClass} border border-sky-200/90 bg-gradient-to-br from-sky-50 to-sky-100/80 p-4 shadow-sm`,
  teal: `${radiusSurfaceClass} border border-teal-200/90 bg-gradient-to-br from-teal-50 to-teal-100/80 p-4 shadow-sm`,
  amber: `${radiusSurfaceClass} border border-amber-200/90 bg-gradient-to-br from-amber-50 to-amber-100/75 p-4 shadow-sm`,
  violet: `${radiusSurfaceClass} border border-violet-200/90 bg-gradient-to-br from-violet-50 to-violet-100/80 p-4 shadow-sm`,
  rose: `${radiusSurfaceClass} border border-rose-200/90 bg-gradient-to-br from-rose-50 to-rose-100/80 p-4 shadow-sm`,
};

const SETTINGS_SECTION_TITLE: Record<SettingsSectionTone, string> = {
  neutral: 'text-sm font-semibold text-[var(--text-primary)]',
  soft: 'text-sm font-semibold text-[var(--text-primary)]',
  sky: 'text-sm font-semibold text-sky-950',
  teal: 'text-sm font-semibold text-teal-950',
  amber: 'text-sm font-semibold text-amber-950',
  violet: 'text-sm font-semibold text-violet-950',
  rose: 'text-sm font-semibold text-rose-950',
};

const SETTINGS_SECTION_HINT: Record<SettingsSectionTone, string> = {
  neutral: 'text-xs text-[var(--text-secondary)]',
  soft: 'text-xs text-[var(--text-secondary)]',
  sky: 'text-xs text-sky-900/75',
  teal: 'text-xs text-teal-900/75',
  amber: 'text-xs text-amber-900/75',
  violet: 'text-xs text-violet-900/75',
  rose: 'text-xs text-rose-900/75',
};

export function settingsSectionShellClass(tone: SettingsSectionTone = 'neutral') {
  return SETTINGS_SECTION_SHELL[tone];
}

export function settingsSectionTitleClass(tone: SettingsSectionTone = 'neutral') {
  return SETTINGS_SECTION_TITLE[tone];
}

export function settingsSectionHintClass(tone: SettingsSectionTone = 'neutral') {
  return SETTINGS_SECTION_HINT[tone];
}

/**
 * Settings page: fill remaining viewport so the tab panel can scroll internally
 * while Save stays pinned. Offset ≈ app header + staff main padding + hint slack.
 */
export const settingsPageClass =
  `flex min-h-0 flex-1 flex-col gap-2 h-[calc(100dvh-7.75rem)] max-h-[calc(100dvh-7.75rem)] overflow-hidden md:h-[calc(100dvh-8.25rem)] md:max-h-[calc(100dvh-8.25rem)]`;

/** App shell <main> — client/guest storefront (roomier vertical rhythm). */
export const appMainClass =
  'mx-auto flex w-full min-h-0 max-w-5xl flex-1 flex-col px-4 py-6';

/** Tenant staff admin — tight top under nav; title lives in nav pills. */
export const appMainStaffClass =
  'mx-auto flex w-full min-h-0 max-w-5xl flex-1 flex-col overflow-hidden px-4 pt-1 pb-2';

export const appMainLivingAlbumClass =
  'flex w-full min-h-0 flex-1 flex-col overflow-x-hidden px-2 py-6 md:px-4';

/** Settings tab panel chrome (nav sibling). */
export const settingsTabPanelClass =
  `${radiusSurfaceClass} flex min-h-0 flex-1 flex-col overflow-hidden border border-[var(--border-color)] bg-[var(--bg-primary)] p-5`;

/** Pinned Save footer below the scroll area inside SettingsTabShell. */
export const settingsTabFooterClass = 'shrink-0 bg-[var(--bg-primary)]';

/** Scrollable body inside a settings tab (Save footer stays outside). */
export const settingsTabScrollClass =
  'scroll-area min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain';

/** Sticky app shell header (language left, actions right, nav below). */
export const shellHeaderClass =
  'sticky top-0 z-10 border-b border-[var(--border-color)] bg-[var(--bg-primary)]/95 backdrop-blur';

export const shellHeaderInnerClass = 'mx-auto max-w-5xl px-4 py-1.5';

export const shellHeaderTopRowClass = 'flex flex-wrap items-center justify-between gap-1.5';

/** Gap between header top row and nav block below. */
export const shellHeaderNavGapClass = 'mt-1';

/** Client storefront footer — manufacturer contacts from landing settings. */
export const storefrontFooterClass =
  'mt-auto shrink-0 border-t border-[var(--border-color)] bg-[var(--bg-primary)]';

/** Secondary pill in header (Back, Logout). */
export const headerPillSecondaryClass =
  'inline-flex h-8 items-center justify-center rounded-full border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-[10px] font-bold uppercase tracking-widest text-[var(--text-secondary)] transition-colors hover:border-[var(--accent-primary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--accent-primary)] sm:h-9 sm:px-4';

/** Context home control in header trailing (compact icon on mobile). */
export const headerHomeLinkClass =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--border-color)] bg-[var(--bg-primary)] p-0 leading-none text-[var(--text-secondary)] transition-colors hover:border-[var(--accent-primary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--accent-primary)] [&_svg]:block [&_svg]:shrink-0 sm:h-9 sm:w-auto sm:gap-1.5 sm:px-3 sm:py-2 sm:text-[10px] sm:font-bold sm:uppercase sm:tracking-widest';

/** Primary pill in header (Login on landing). */
export const headerPillPrimaryClass =
  'inline-flex h-8 items-center justify-center rounded-full border border-transparent bg-[var(--accent-primary)] px-3 text-[10px] font-bold uppercase tracking-widest text-[var(--accent-fg,#fff)] transition-colors hover:brightness-95 sm:h-9 sm:px-4';

/** Centered horizontal tab row in header. */
export const navRowClass = 'flex justify-center gap-3 overflow-x-auto pb-0.5 sm:gap-5';

/** Platform admin nav — variable-width tabs, wrap when the row is full. */
export const platformNavRowClass =
  'flex flex-wrap justify-center gap-1.5 overflow-x-auto pb-0.5 sm:gap-2';

/** @deprecated Platform tabs no longer use equal fixed width — kept for reference. */
export const navTabFixedWidthClass = 'w-[10.5rem]';

const navTabBaseClass =
  'inline-flex shrink-0 items-center justify-center whitespace-nowrap border text-center transition-colors';

/** Platform admin tabs — variable width, rounded-xl; wraps in platformNavRowClass. */
export function navTabPlatformClass(isActive: boolean) {
  return `${navTabBaseClass} ${radiusControlClass} px-3 py-1.5 text-xs font-medium sm:px-3.5 sm:py-2 sm:text-sm ${
    isActive
      ? 'border-transparent bg-[var(--accent-primary)] text-white'
      : 'border-transparent text-[var(--text-secondary)] hover:border-[var(--accent-primary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--accent-primary)]'
  }`;
}

/** App shell nav tabs — variable width, icon + label, rounded-2xl. */
export function navTabAppClass(isActive: boolean) {
  return `inline-flex shrink-0 items-center gap-2 ${radiusSurfaceClass} border px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors sm:px-4 ${
    isActive
      ? 'border-transparent bg-[var(--accent-primary)] text-white'
      : 'border-transparent bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]'
  }`;
}

/** App nav row — slightly tighter gap when many items. */
export const appNavRowClass = 'flex justify-center gap-2 overflow-x-auto pb-1';

/** Tenant slot-picker overlay — portaled to body (escapes sticky header + overflow). */
export const navSlotDropdownOverlayClass =
  'fixed inset-0 z-[200] bg-[var(--slot-picker-overlay)] backdrop-blur-[2px]';

export const navSlotDropdownClass =
  `fixed z-[210] flex min-w-[14rem] flex-col gap-1 overflow-hidden ${radiusSurfaceClass} border-2 border-[var(--slot-picker-tray-border)] bg-[var(--slot-picker-tray-bg)] p-2 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.28)] ring-2 ring-[var(--slot-picker-tray-ring)]`;

export function navSlotDropdownItemClass(isPicked: boolean) {
  return `flex items-center gap-3 ${radiusControlClass} p-3 text-left transition-colors ${
    isPicked
      ? 'bg-[var(--bg-secondary)] text-[var(--accent-primary)]'
      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'
  }`;
}

export const navSlotGroupClass =
  `inline-flex items-stretch ${radiusSurfaceClass} border shadow-sm transition-colors`;

export const navSlotGroupActiveClass =
  'border-transparent bg-[var(--accent-primary)] text-white';

export const navSlotGroupInactiveClass =
  'border-transparent bg-[var(--bg-secondary)] text-[var(--text-secondary)]';

export function navSlotMainButtonClass(isActive: boolean) {
  return `inline-flex shrink-0 items-center gap-2 rounded-l-[var(--radius-surface)] border px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors sm:px-4 ${
    isActive
      ? 'border-transparent hover:bg-white/10'
      : 'border-transparent hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]'
  }`;
}

export function navSlotToggleButtonClass(isActive: boolean) {
  return `inline-flex min-w-[2.25rem] items-center justify-center rounded-r-[var(--radius-surface)] border-l px-2 py-2 transition-colors ${
    isActive
      ? 'border-white/25 hover:bg-white/10'
      : 'border-[var(--border-color)]/60 hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]'
  }`;
}

/** AppDialog on /app mobile — sit above fixed bottom nav with a small gap (FLPBox-style). */
export const appDialogOverlayAboveMobileNavClass =
  'max-md:items-end max-md:justify-center max-md:px-4 max-md:pb-[calc(var(--app-mobile-bottom-nav)+0.5rem)] max-md:pt-4';

export const appDialogShellAboveMobileNavClass =
  'max-md:max-h-[calc(100dvh-var(--app-mobile-bottom-nav)-1.25rem)]';

/** Fixed bottom tab bar — mobile only (FLPBox MobileBottomNav). */
export const mobileBottomNavClass =
  'fixed bottom-0 left-0 right-0 z-[200] border-t border-[var(--border-color)] bg-[var(--bg-primary)]/90 px-2 pb-safe backdrop-blur-xl';

export const mobileBottomNavRowClass = 'flex items-center justify-around pt-3 pb-6';

export function mobileBottomNavItemClass(isActive: boolean) {
  return `relative flex flex-1 flex-col items-center justify-center gap-1 ${radiusControlClass} px-2 py-1 transition-colors select-none ${
    isActive
      ? 'bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]'
      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--accent-primary)]'
  }`;
}

/** Vertical slot tray above mobile bottom bar. */
export const mobileSlotTrayClass =
  `fixed z-[210] flex flex-col-reverse items-center gap-2 ${radiusDialogClass} border-2 border-[var(--slot-picker-tray-border)] bg-[var(--slot-picker-tray-bg)] p-3 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.28)] ring-2 ring-[var(--slot-picker-tray-ring)]`;

export function mobileSlotTrayItemClass(isPicked: boolean) {
  return `flex h-14 w-14 flex-col items-center justify-center gap-1 ${radiusSurfaceClass} border shadow-lg transition-all active:scale-90 select-none ${
    isPicked
      ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)] text-white'
      : 'border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-secondary)]'
  }`;
}

/** Language switcher — three standalone circles, no outer track. */
export const languageSwitcherRowClass = 'flex shrink-0 items-center gap-1';

export function languageSwitcherButtonClass(isActive: boolean) {
  return `inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold uppercase transition-colors sm:h-9 sm:w-9 ${
    isActive
      ? 'border-transparent bg-[var(--accent-primary)] text-[var(--accent-fg,#fff)]'
      : 'border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:border-[var(--accent-primary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--accent-primary)]'
  }`;
}

/** Content width aligned with header. */
export const pageMainClass = 'mx-auto max-w-5xl px-4 py-8';

/** Icon row actions (Edit, Open, Suspend…) — accent border + icon on hover. */
export const iconActionHoverClass =
  'hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]';

/** Vertical divider between table columns — skip the first column. */
export const tableColDividerClass = 'border-l border-[var(--border-color)]';

/** ── DataTable — global list/table template (platform tenants reference). ── */

export const dataTableShellClass = `overflow-x-auto ${surfacePanelClass}`;

export const dataTableFillShellClass =
  `flex min-h-0 flex-1 flex-col overflow-hidden ${surfacePanelClass} shadow-sm`;

export const dataTableScrollClass = 'scroll-area min-h-0 flex-1 overflow-y-auto overflow-x-auto';

export const dataTableClass = 'w-full text-sm';

export const dataTableFillClass = `h-full min-h-full table-auto ${dataTableClass}`;

export const dataTableCellClass = 'px-4 py-2 text-center align-middle';

/** Head cells carry label typography (uppercase) so sort buttons match plain headers. */
export const dataTableHeadCellClass =
  `${dataTableCellClass} bg-[var(--bg-secondary)] text-xs uppercase text-[var(--text-secondary)]`;

/** Row background for thead — cell typography lives on dataTableHeadCellClass. */
export const dataTableHeadClass = 'bg-[var(--bg-secondary)]';

export const dataTableStickyHeadClass = `${dataTableHeadClass} sticky top-0 z-[1]`;

export const dataTableRowClass = 'border-t border-[var(--border-color)]';

export const dataTableRowHoverClass =
  `group/row ${dataTableRowClass} transition-colors hover:bg-[var(--bg-secondary)]/40`;

export const dataTableActionsClass = 'inline-flex flex-nowrap items-center justify-center gap-1.5';

/** Slug, SKU, codes — same as tenant slug column. */
export const dataTableCodeClass = 'font-mono text-xs';

/** Keep edit/delete visible when the table scrolls horizontally. */
export const dataTableActionsColClass =
  'sticky right-0 z-[1] whitespace-nowrap bg-[var(--bg-primary)] shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)] group-hover/row:bg-[var(--bg-secondary)]/40';

export const dataTableActionsHeadColClass =
  'sticky right-0 z-[2] whitespace-nowrap bg-[var(--bg-secondary)] shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]';

export const dataTableThumbColClass = 'w-12 sm:w-16';

export const dataTableThumbClass =
  `mx-auto flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden ${radiusControlClass} bg-[var(--bg-secondary)] p-1 sm:h-14 sm:w-14`;

export const dataTableBodyFillClass =
  'flex min-h-full flex-col items-center justify-center px-4 py-12 text-center';

export function dataTableSortButtonClass(isActive: boolean) {
  return `inline-flex max-w-full items-center justify-center gap-1 text-xs uppercase transition-colors hover:text-[var(--accent-primary)] ${
    isActive ? 'text-[var(--accent-primary)]' : 'text-[var(--text-secondary)]'
  }`;
}

export const iconActionBaseClass =
  `inline-flex h-9 w-9 shrink-0 items-center justify-center ${radiusControlClass} border shadow-sm transition-colors`;

/** Text action buttons on colored maintenance panels. */
export const panelActionButtonClass =
  `${radiusControlClass} border border-[var(--border-color)] bg-white/80 px-4 py-2 text-sm font-medium text-[var(--text-secondary)] shadow-sm transition-colors hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] disabled:opacity-60`;

/** Destructive panel action (e.g. clear audit log). */
export const panelActionDangerButtonClass =
  `${radiusControlClass} border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 shadow-sm transition-colors hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] disabled:opacity-60`;

/** Colored panel tones — use MetricCard / MaintenancePanel components. */
export type UiPanelTone = 'sky' | 'violet' | 'amber' | 'teal' | 'rose';

/** Products list page layout (toolbar + fill-height DataTable). */
export const productPageClass = 'flex min-h-0 flex-1 flex-col gap-2';

/** Screen hint under app nav when the page title is omitted (tenant admin). */
export const adminPageHintWrapClass = 'shrink-0';
export const adminPageHintClass = 'text-sm leading-relaxed text-[var(--text-secondary)]';

export const productListToolbarClass =
  'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end';

export const productAddButtonClass =
  `inline-flex min-h-9 items-center justify-center gap-2 ${radiusControlClass} bg-[var(--accent-primary)] px-6 text-xs font-normal tracking-wide text-[var(--bg-primary)] shadow-lg shadow-[var(--accent-primary)]/20 transition-all hover:opacity-90 active:scale-[0.98]`;

/** Primary submit/save in forms and settings panels (matches AppDialog footer). */
export const formSaveButtonClass =
  `${radiusControlClass} bg-[var(--accent-primary)] px-4 py-2 text-sm font-semibold text-[var(--accent-fg,#fff)] transition-all hover:brightness-95 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60`;

/** Centered column for profile info and password forms (staff + client). */
export const profileFormColumnClass = 'mx-auto w-full max-w-2xl';

/** Client profile shell — wider area for order history table. */
export const profilePageShellClass = 'mx-auto w-full max-w-5xl';

/** Secondary cancel in AppDialog footers (matches ConfirmDialog). */
export const dialogDismissButtonClass =
  'absolute end-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-transparent text-[var(--text-secondary)] transition-colors hover:border-[var(--border-color)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]';

export const formCancelButtonClass =
  `${radiusControlClass} border border-[var(--border-color)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] transition-colors hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] disabled:opacity-60`;

/** Destructive confirm in AppDialog / ConfirmDialog. */
export const formDestructiveButtonClass =
  `${radiusControlClass} bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-60`;

/** Compact tab inside a segmented control (CRM, import, notes). */
export function pageTabButtonClass(isActive: boolean) {
  return `${radiusControlClass} px-3 py-1.5 text-sm transition-colors ${
    isActive
      ? 'bg-[var(--accent-primary)] text-white'
      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]'
  }`;
}

/** Outline button — pagination, secondary panel actions. */
export const outlineButtonClass =
  `${radiusControlClass} border border-[var(--border-color)] px-3 py-1.5 text-sm font-medium text-[var(--text-secondary)] transition-colors hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] disabled:opacity-50`;

/** Square icon control in catalog pagination (first / last page). */
export const paginationIconButtonClass =
  `${outlineButtonClass} inline-flex h-9 w-9 shrink-0 items-center justify-center p-0`;

/** Catalog page switcher row — prev/next + page indicator. */
export const catalogPaginationClass = 'flex flex-wrap items-center justify-center gap-2';

/** Floating scroll-to-top on long catalog pages (above mobile bottom nav). */
export const scrollToTopFabClass =
  `fixed right-4 z-[190] flex h-11 w-11 items-center justify-center rounded-full border border-[var(--border-color)] bg-[var(--bg-primary)]/95 text-[var(--text-secondary)] shadow-lg backdrop-blur transition-opacity hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] bottom-[calc(var(--app-mobile-bottom-nav)+0.75rem)] md:bottom-6`;

/** Pull-to-refresh spinner — fixed under the sticky app header on mobile. */
export const pullToRefreshIndicatorClass =
  'pointer-events-none fixed inset-x-0 top-[calc(env(safe-area-inset-top,0px)+3.25rem)] z-[185] flex justify-center transition-[transform,opacity] duration-150 md:hidden';

/** Visiting card /c: no app header — keep the spinner at the top edge, below lang chips. */
export const visitingCardPullToRefreshIndicatorClass =
  'pointer-events-none fixed inset-x-0 top-[calc(env(safe-area-inset-top,0px)+0.5rem)] z-[25] flex justify-center transition-[transform,opacity] duration-150 md:hidden';

/** Product modal — mobile bottom sheet above tab bar. */
export const catalogProductModalOverlayClass =
  'max-md:items-end max-md:justify-center max-md:p-0 max-md:pb-[calc(var(--app-mobile-bottom-nav)+0.25rem)]';

export const catalogProductModalShellClass =
  'max-md:max-h-[calc(100dvh-var(--app-mobile-bottom-nav)-0.5rem)] max-md:w-full max-md:max-w-none max-md:rounded-b-none max-md:rounded-t-[var(--radius-dialog)]';

/** Prevent image drag, selection, and callout in product modal media areas. */
export const catalogProductModalMediaProtectClass =
  'select-none [-webkit-user-select:none] [-webkit-touch-callout:none] [&_img]:pointer-events-none [&_img]:select-none [&_img]:[-webkit-user-drag:none]';

/** Full-bleed product hero (square, capped on very tall viewports). */
export const catalogProductModalHeroClass =
  'relative w-full overflow-hidden bg-transparent aspect-square max-h-[min(100vw,58dvh)]';

/** Horizontal snap track for product photos. */
export const catalogProductModalHeroTrackClass =
  'flex h-full w-full snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

export const catalogProductModalHeroDesktopClass =
  'md:aspect-[5/6] md:min-h-[22rem] md:max-h-[min(72vh,34rem)] md:rounded-[var(--radius-control)] lg:min-h-[26rem]';

/** Full dialog-shell photo zoom (desktop product modal). */
export const catalogProductModalPhotoZoomLayerClass =
  'absolute inset-0 z-50 hidden flex-col overflow-hidden bg-[var(--bg-primary)] md:flex';

export const catalogProductModalPhotoZoomDismissClass =
  'absolute end-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] shadow-sm transition-colors hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]';

/** Close control floating on the mobile product hero. */
export const catalogProductModalHeroDismissClass =
  'absolute end-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border-color)] bg-[var(--bg-primary)]/92 text-[var(--text-secondary)] shadow-md backdrop-blur transition-colors hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]';

/** Larger gallery thumbs in the product modal (mobile-first). */
export function catalogProductModalThumbButtonClass(active: boolean) {
  return `h-[4.5rem] w-[4.5rem] shrink-0 overflow-hidden sm:h-20 sm:w-20 ${radiusControlClass} border-2 transition-colors ${
    active
      ? 'border-[var(--accent-primary)]'
      : 'border-[var(--border-color)] hover:border-[var(--accent-primary)]'
  }`;
}

export const catalogProductModalDotClass =
  'h-1.5 w-1.5 rounded-full transition-colors';

export const catalogProductModalMobileFooterClass =
  'flex flex-wrap items-center justify-between gap-3 max-md:justify-between md:justify-center';

/** Circular clear control inside search fields. */
export const searchClearButtonClass =
  'absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--border-color)] bg-[var(--bg-primary)]/90 text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]';

/** Stepper +/- on cart and similar quantity controls. */
export const quantityStepButtonClass =
  `${radiusControlClass} flex h-8 w-8 items-center justify-center border border-[var(--border-color)] transition-colors hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]`;

/** Text-only accent action (reset filters, clear all). */
export const accentTextButtonClass =
  `${radiusControlClass} shrink-0 px-2 py-1 text-xs font-medium text-[var(--accent-primary)] transition-colors hover:bg-[var(--accent-primary)]/10`;

/** Close icon in filter sheet header. */
export const filterSheetCloseButtonClass =
  'flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border-color)] text-[var(--text-secondary)] transition-colors hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]';

/** Secondary landing / marketing CTA link styling. */
export const landingSecondaryLinkClass =
  `${radiusSurfaceClass} border border-[var(--border-color)] bg-[var(--bg-secondary)] px-5 py-2.5 text-sm font-semibold transition-colors hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] sm:px-6 sm:py-3`;

/** Platform landing section — light accent wash. */
export const landingAccentPanelClass =
  `${radiusSurfaceClass} border border-[var(--accent-primary)]/20 bg-gradient-to-br from-[var(--accent-primary)]/[0.07] via-[var(--bg-primary)] to-[var(--bg-primary)] shadow-sm shadow-[var(--accent-primary)]/10`;

/** Featured platform landing section (e.g. client directory). */
export const landingAccentPanelEmphasisClass =
  `${radiusSurfaceClass} border-2 border-[var(--accent-primary)]/35 bg-gradient-to-br from-[var(--accent-primary)]/[0.14] via-[var(--accent-primary)]/[0.06] to-[var(--bg-primary)] shadow-md shadow-[var(--accent-primary)]/15`;

/** Large primary CTA (login, landing). */
export const ctaButtonClass =
  `${radiusSurfaceClass} bg-[var(--accent-primary)] px-4 py-3 text-sm font-semibold text-white transition-all hover:brightness-95 active:scale-[0.98] disabled:opacity-60`;

export function filterTagChipClass(active: boolean) {
  return `${radiusControlClass} border px-2.5 py-1 text-xs transition-colors ${
    active
      ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]'
      : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]'
  }`;
}

export function catalogImageThumbButtonClass(active: boolean) {
  return `h-14 w-14 shrink-0 overflow-hidden ${radiusControlClass} border-2 transition-colors ${
    active
      ? 'border-[var(--accent-primary)]'
      : 'border-[var(--border-color)] hover:border-[var(--accent-primary)]'
  }`;
}

/** Toggle visibility inside a full-width dialog password field. */
export const dialogPasswordToggleClass =
  `absolute inset-y-0 right-0 flex items-center px-3 text-xs font-medium text-[var(--text-secondary)] transition-colors hover:text-[var(--accent-primary)] disabled:opacity-60`;

export const productSearchWrapClass = 'relative group';

export const productSearchInputClass =
  `w-full ${radiusControlClass} border border-[var(--border-color)] bg-[var(--bg-primary)] py-2.5 pl-11 pr-10 text-sm text-[var(--text-primary)] shadow-sm transition-all focus:border-[var(--accent-primary)]/30 focus:outline-none focus:ring-4 focus:ring-[var(--accent-primary)]/10`;

export const productEmptyIconClass = 'mb-4 text-[var(--text-secondary)] opacity-20';

/** Column template only — set `gap` via style (e.g. catalogSite.productGapPx). */
export const catalogGridColsClass =
  'grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4';

export const productLocaleDotClass = (filled: boolean) =>
  `h-2 w-2 rounded-full border ${
    filled
      ? 'border-emerald-400/70 bg-emerald-500'
      : 'border-[var(--border-color)] bg-[var(--bg-secondary)]'
  }`;

/** Storefront catalog layout. */
export const catalogLayoutClass = 'flex min-h-0 flex-1 flex-col gap-4 lg:flex-row lg:gap-6';

export const catalogSidebarClass =
  `shrink-0 lg:w-52 ${radiusSurfaceClass} border border-[var(--border-color)] bg-[var(--bg-primary)] p-3`;

export const catalogMainClass = 'flex min-h-0 min-w-0 flex-1 flex-col gap-4';

export const catalogGridClass =
  'grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4';

/** Denser product grid for desktop website-style catalog layout. */
export const catalogGridWebsiteClass =
  'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6';

export const catalogTopNavClass =
  'hidden flex-col gap-2 border-b border-[var(--border-color)] pb-3 lg:flex';

export function catalogTopNavLinkClass(active: boolean) {
  return `border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
    active
      ? 'border-[var(--accent-primary)] text-[var(--accent-primary)]'
      : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
  }`;
}

export function catalogTopNavSubLinkClass(active: boolean) {
  return `${radiusControlClass} px-2.5 py-1 text-xs font-medium transition-colors ${
    active
      ? 'bg-[color-mix(in_srgb,var(--accent-primary)_12%,transparent)] text-[var(--accent-primary)]'
      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]'
  }`;
}

export const catalogProductModalWebsiteClass = 'lg:max-w-[min(72vw,60rem)]';

export function catalogCardClass() {
  return `${radiusSurfaceClass} flex h-full flex-col overflow-hidden border border-[var(--border-color)] bg-[var(--bg-primary)] text-left shadow-sm transition-shadow hover:shadow-md`;
}

export const catalogCardImageClass = 'aspect-square w-full bg-transparent object-cover';

export const catalogCardBodyClass = 'flex flex-col gap-1 px-2 pt-1.5 pb-2';

/** Website catalog grid cards — centered preview / price / title (lg+ only). */
export const catalogCardBodyWebsiteClass =
  'hidden flex-col items-center gap-1.5 px-2 pt-1.5 pb-2 text-center lg:flex';

export function catalogCardClassWebsite() {
  return `${catalogCardClass()} lg:text-center`;
}

export const catalogCategoryChipClass = (active: boolean) =>
  `${radiusControlClass} whitespace-nowrap border px-3 py-1.5 text-xs font-medium transition-colors ${
    active
      ? 'border-transparent bg-[var(--accent-primary)] text-white'
      : 'border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]'
  }`;

export type CatalogPromotionChipVariant = 'active' | 'endingSoon' | 'scheduled';

export const catalogPromotionChipClass = (variant: CatalogPromotionChipVariant, selected: boolean) => {
  const base = `${radiusControlClass} flex min-w-[7rem] flex-col items-start gap-0.5 border px-3 py-2 text-left text-xs transition-colors`;

  if (variant === 'scheduled') {
    return `${base} border-sky-200/80 bg-sky-50 text-sky-950 shadow-sm hover:border-sky-400 hover:bg-sky-100`;
  }

  if (variant === 'endingSoon') {
    return selected
      ? `${base} border-transparent bg-rose-600 text-white shadow-sm shadow-rose-600/25`
      : `${base} border-rose-200 bg-rose-50 text-rose-950 hover:border-rose-400 hover:bg-rose-100`;
  }

  return selected
    ? `${base} border-transparent bg-[var(--accent-primary)] text-white shadow-sm shadow-[var(--accent-primary)]/20`
    : `${base} border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]`;
};

const catalogCategoryDepthPad = (depth: number) => {
  if (depth <= 0) return '';
  if (depth === 1) return 'ps-5';
  if (depth === 2) return 'ps-9';
  if (depth === 3) return 'ps-[2.75rem]';
  return 'ps-14';
};

export const catalogCategoryTreeButtonClass = (active: boolean, depth: number) =>
  `block w-full ${radiusControlClass} px-2 py-1.5 text-left text-sm transition-colors ${
    active
      ? 'bg-[var(--accent-primary)]/10 font-medium text-[var(--accent-primary)]'
      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]'
  } ${catalogCategoryDepthPad(depth)}`;

/** Mobile catalog filter bottom sheet — above mobile bottom nav (z 200). */
export const catalogFilterSheetOverlayClass =
  'fixed inset-0 z-[220] bg-[var(--slot-picker-overlay)] backdrop-blur-[2px] lg:hidden';

/** Mobile catalog filter bottom sheet panel. */
export const catalogFilterSheetPanelClass =
  `fixed inset-x-0 bottom-0 z-[221] flex max-h-[min(92dvh,720px)] flex-col overflow-hidden ${radiusDialogClass} border border-[var(--border-color)] border-b-0 bg-[var(--bg-primary)] shadow-[0_-16px_48px_-12px_rgba(0,0,0,0.2)] lg:hidden`;

export const catalogFilterSheetScrollClass =
  'scroll-area min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4';

export const catalogFilterSheetBodyClass =
  'flex flex-col gap-6 pb-[calc(5rem+env(safe-area-inset-bottom,0px))]';

export const catalogFilterSheetFooterClass =
  'flex shrink-0 gap-2 border-t border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))]';

export const catalogFilterSectionTitleClass =
  'text-[10px] font-bold uppercase tracking-widest text-[var(--text-secondary)]';

export const catalogFilterChipClass = (active: boolean, disabled = false) =>
  `${radiusControlClass} border px-2.5 py-1 text-xs font-medium transition-colors ${
    disabled
      ? 'cursor-not-allowed border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-secondary)] opacity-50'
      : active
        ? 'border-transparent bg-[var(--accent-primary)] text-white'
        : 'border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]'
  }`;

export const catalogFilterToolbarButtonClass =
  `${radiusControlClass} inline-flex items-center gap-2 border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)] transition-colors hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)] lg:hidden`;

export const catalogActiveFiltersRowClass = 'flex flex-wrap items-center gap-2';

export const catalogActiveFilterChipClass =
  `${radiusControlClass} inline-flex items-center gap-1 border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1 text-xs text-[var(--text-primary)] transition-colors hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]`;

export const catalogSidebarAttributeBlockClass = 'mt-4 border-t border-[var(--border-color)] pt-3';
